// useAppMemberships — read & cache the calling user's app_memberships
// list (F1 GET /api/app-memberships/me) plus the static F4 catalog
// (GET /api/app-memberships/catalog). Single source of truth for the
// HubAppSwitcher and MyAppsSettings page.

import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export const useAppMemberships = (auto = true) => {
  const [memberships, setMemberships] = useState(null);
  const [catalog, setCatalog] = useState(null);
  // Phase A6-prod (Feb 2026) — when `HUB_CATALOG_ENDPOINT_ENABLED=false`
  // (Production conservative setup), the catalog endpoint returns 404.
  // We surface this as a soft signal so the UI can render a clean
  // "coming soon" placeholder instead of an error overlay.
  const [catalogDisabled, setCatalogDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    // Landing-bugfix-01 (Feb 2026): defense in depth — if there is no
    // auth token at all (e.g. the switcher is mounted on a public
    // page), do NOT issue any network call. `/me` requires auth and
    // would 401, which the global axios error path could escalate to
    // a runtime overlay on the public landing.
    const hasToken = !!localStorage.getItem("tsw_token");
    if (!hasToken) {
      setMemberships([]);
      setCatalog([]);
      setCatalogDisabled(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Phase A6-prod (Feb 2026) — fetch memberships and catalog
      // INDEPENDENTLY. A 404 on /catalog (HUB_CATALOG_ENDPOINT_ENABLED=
      // false) must NOT break /me. Both calls have their own try block.
      const [mineRes, catRes] = await Promise.allSettled([
        api.get("/app-memberships/me"),
        api.get("/app-memberships/catalog"),
      ]);
      if (mineRes.status === "fulfilled") {
        setMemberships(mineRes.value.data?.items || []);
      } else {
        // /me failure is a real error; bubble it up.
        setError(mineRes.reason);
        setMemberships([]);
      }
      if (catRes.status === "fulfilled") {
        setCatalog(catRes.value.data?.apps || []);
        setCatalogDisabled(false);
      } else {
        // 404 → endpoint flag is OFF (expected in conservative Prod).
        // Any other status → treat the same so the UI never crashes.
        setCatalog([]);
        setCatalogDisabled(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) refresh();
    else setIsLoading(false);
  }, [auto, refresh]);

  return { memberships, catalog, catalogDisabled, isLoading, error, refresh };
};

export default useAppMemberships;
